/**
 * View for the curated empty-query sections.
 *
 * Renders "Recently Viewed" and "Upside Potential" sections stacked
 * vertically inside a scrollable column. Each section is its own
 * Container/View pair (`CuratedSection`).
 * @module features/global-search/components/GlobalSearchCuratedSections/GlobalSearchCuratedSectionsView
 */

import React, { memo } from "react";

import { ScrollView } from "@/components/atoms/ScrollView";
import { type PlayerSearchPlayerFragment } from "@/generated/graphql";

import CuratedSection from "./CuratedSection";

/** Props for the curated sections view */
interface GlobalSearchCuratedSectionsViewProps {
  readonly recentPlayers: readonly PlayerSearchPlayerFragment[];
  readonly upsidePlayers: readonly PlayerSearchPlayerFragment[];
  readonly recentLoading: boolean;
  readonly upsideLoading: boolean;
  readonly onPlayerPress: (player: PlayerSearchPlayerFragment) => void;
  readonly renderAction: (
    player: PlayerSearchPlayerFragment
  ) => React.ReactNode;
  readonly effectiveSortBy: string;
  readonly sortFieldLabel: string;
}

/** ScrollView contentContainerStyle hoisted out so the View body stays declarative */
const SCROLL_CONTENT_STYLE = { paddingBottom: 24 } as const;

/**
 * Renders the curated sections inside a scrollable column
 * @param root0 - Component props
 * @param root0.recentPlayers - Players for Recently Viewed
 * @param root0.upsidePlayers - Players for Upside Potential
 * @param root0.recentLoading - Whether Recently Viewed is loading
 * @param root0.upsideLoading - Whether Upside Potential is loading
 * @param root0.onPlayerPress - Row press handler
 * @param root0.renderAction - Action slot renderer per row
 * @param root0.effectiveSortBy - Active sort field for the metric badge
 * @param root0.sortFieldLabel - Sort field display label
 */
const GlobalSearchCuratedSectionsView = ({
  recentPlayers,
  upsidePlayers,
  recentLoading,
  upsideLoading,
  onPlayerPress,
  renderAction,
  effectiveSortBy,
  sortFieldLabel,
}: GlobalSearchCuratedSectionsViewProps) => (
  <ScrollView
    className="flex-1"
    contentContainerStyle={SCROLL_CONTENT_STYLE}
    showsVerticalScrollIndicator={false}
  >
    <CuratedSection
      title="Recently Viewed"
      players={recentPlayers}
      loading={recentLoading}
      onPlayerPress={onPlayerPress}
      renderAction={renderAction}
      effectiveSortBy={effectiveSortBy}
      sortFieldLabel={sortFieldLabel}
    />
    <CuratedSection
      title="Upside Potential"
      players={upsidePlayers}
      loading={upsideLoading}
      onPlayerPress={onPlayerPress}
      renderAction={renderAction}
      effectiveSortBy={effectiveSortBy}
      sortFieldLabel={sortFieldLabel}
    />
  </ScrollView>
);

GlobalSearchCuratedSectionsView.displayName = "GlobalSearchCuratedSectionsView";

export default memo(GlobalSearchCuratedSectionsView);
